import { useCallback, useEffect, useRef, useState } from 'react';
import {
  needsAt,
  type FlightView,
  type FlyRequest,
  type PlayState,
  type TrainRequest,
  type TravelMode,
} from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import { api } from '../lib/api';

// The player's live game state. The server's reply is the truth; between replies the needs bars fall with the
// same formula the server uses, so the screen never shows a number the server would not. As in Lagos Life,
// things tapped while the character is busy doing something wait in a queue (three at most) and start one after
// another; a queued one can be taken off, and the one under way stopped.
const QUEUE_MAX = 3;
export type Queued = { uid: string; activityId: string; choice?: string };
export function usePlay(enabled: boolean) {
  const [state, setState] = useState<PlayState | null>(null);
  const [received, setReceived] = useState(() => new Date());
  const [now, setNow] = useState(() => new Date());
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);
  const [queue, setQueue] = useState<Queued[]>([]);
  const queued = useRef<Queued[]>([]);
  queued.current = queue;

  // the newest reply, readable straight after an action (before the screen has drawn it)
  const newest = useRef<PlayState | null>(null);
  const accept = useCallback((next: { state: PlayState; message?: string }) => {
    newest.current = next.state;
    setState(next.state);
    setReceived(new Date());
    if (next.message) setMessage(next.message);
  }, []);
  const refresh = useCallback(async () => {
    try {
      accept(await api.play.state());
    } catch (error) {
      setFailure(describeError(error));
    }
  }, [accept]);
  useEffect(() => {
    if (enabled) void refresh();
    else setState(null);
  }, [enabled, refresh]);
  useEffect(() => {
    if (!state) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [state]);
  // when an action finishes on the server's clock, ask again so the screen moves on
  useEffect(() => {
    if (!state?.busyUntil) return;
    const wait = new Date(state.busyUntil).getTime() - Date.now();
    const timer = setTimeout(() => void refresh(), Math.max(wait, 0) + 300);
    return () => clearTimeout(timer);
  }, [state?.busyUntil, refresh]);

  // every action gets its own request key: a double tap or a retry happens once
  const run = useCallback(
    async (send: (key: string) => Promise<{ state: PlayState; message?: string }>) => {
      setBusy(true);
      setFailure(null);
      try {
        accept(await send(`play-${crypto.randomUUID()}`));
        return true;
      } catch (error) {
        setFailure(describeError(error));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [accept],
  );
  // setting off somewhere drops what was waiting
  const travel = useCallback(
    (placeId: string, mode: TravelMode) => {
      setQueue([]);
      return run((key) => api.play.travel({ placeId, mode }, key));
    },
    [run],
  );
  // a flight between the game's airports: the flight booked, or null (the reason is the failure)
  const fly = useCallback(
    async (body: FlyRequest) => {
      setQueue([]);
      let booked: FlightView | null = null;
      const ok = await run(async (key) => {
        const reply = await api.play.fly(body, key);
        booked = reply.flight;
        return reply;
      });
      return ok ? booked : null;
    },
    [run],
  );
  // a train from King's Cross: the trip booked, or null (the reason is the failure)
  const train = useCallback(
    async (body: TrainRequest) => {
      setQueue([]);
      let booked: FlightView | null = null;
      const ok = await run(async (key) => {
        const reply = await api.play.train(body, key);
        booked = reply.flight;
        return reply;
      });
      return ok ? booked : null;
    },
    [run],
  );
  const start = useCallback(
    (activityId: string, choice?: string) =>
      run((key) => api.play.act({ activityId, ...(choice ? { choice } : {}) }, key)),
    [run],
  );
  // doing something already: it waits its turn (on the way somewhere, the server says why not)
  const act = useCallback(
    async (activityId: string, choice?: string) => {
      const current = newest.current;
      const doing = current?.doing && current.busyUntil && new Date(current.busyUntil).getTime() > Date.now();
      if (!doing) return start(activityId, choice);
      if (queued.current.length >= QUEUE_MAX) {
        setMessage('Three things are waiting already. Tap one to take it off.');
        return false;
      }
      queued.current = [...queued.current, { uid: crypto.randomUUID(), activityId, ...(choice ? { choice } : {}) }];
      setQueue(queued.current);
      return true;
    },
    [start],
  );
  // on the way somewhere: arrive now
  const skip = useCallback(() => run(() => api.play.skip()), [run]);
  // "× tap to cancel": a waiting one comes off the queue; the one under way stops (the server takes back the rest)
  const cancel = useCallback(
    (uid?: string) => {
      if (uid) return setQueue((list) => list.filter((item) => item.uid !== uid));
      void run(() => api.play.cancel());
    },
    [run],
  );

  const needs = state ? needsAt(state.needs, received, now, state.traits) : null;
  const busyFor = state?.busyUntil
    ? Math.max(0, Math.ceil((new Date(state.busyUntil).getTime() - now.getTime()) / 1000))
    : 0;
  // free again: the next one in the queue starts (a refusal, say too tired, drops it with the reason)
  const next = queue[0];
  useEffect(() => {
    if (!next || busy || busyFor > 0 || !state) return;
    setQueue((list) => list.slice(1));
    void start(next.activityId, next.choice);
  }, [next, busy, busyFor, state, start]);
  return {
    state,
    latest: () => newest.current,
    needs,
    busyFor,
    sending: busy,
    message,
    failure,
    clearMessage: () => setMessage(null),
    clearFailure: () => setFailure(null),
    travel,
    fly,
    train,
    act,
    skip,
    cancel,
    queue,
    refresh,
  };
}
